import * as io from "io-ts";
import createGlobalState from "@ui/lib/createGlobalState";
import t from "../../../i18n";
import { DateTime, Duration, Interval } from "luxon";
import React from "react";
import { nullable } from "diagnostics-lib/io";
import type { ReportingPeriod } from "../../../api/useReporting";

export const DEFAULT_REPORTING_PERIOD = Duration.fromObject({ month: 12 }).toString() as string;

export const defaultReportingPeriodOptions = [
  {
    label: t("reporting.period_selector.3m"),
    value: Duration.fromObject({ month: 3 }).toString() as string,
  },
  {
    label: t("reporting.period_selector.6m"),
    value: Duration.fromObject({ month: 6 }).toString() as string,
  },
  {
    label: t("reporting.period_selector.1y"),
    value: Duration.fromObject({ month: 12 }).toString() as string,
  },
];

export const useReportingPeriod = createGlobalState({
  storage: "session",
  key: "reportingPeriod",
  type: nullable(io.string),
  defaultValue: DEFAULT_REPORTING_PERIOD,
  scoped: false,
});

export function useReportingPeriodOptions(reportingPeriod: ReportingPeriod | null) {
  return React.useMemo(() => {
    if (!reportingPeriod) return [...defaultReportingPeriodOptions];
    const fromDate = DateTime.fromISO(reportingPeriod.min);
    const toDate = DateTime.fromISO(reportingPeriod.max);

    const startOfLastYear = toDate ? DateTime.fromMillis(toDate.toMillis()).startOf("year") : null;

    const lastYearReportingOption =
      fromDate && toDate && startOfLastYear
        ? [
            {
              label: toDate.year.toString(),
              value: Interval.fromDateTimes(startOfLastYear, toDate)
                .toDuration()
                .toString() as string,
            },
          ]
        : [];

    const allTimeReportingOption =
      fromDate && toDate
        ? [
            {
              label: t("reporting.period_selector.all_time"),
              value: Interval.fromDateTimes(fromDate, toDate).toDuration().toString() as string,
            },
          ]
        : [];
    return [
      ...defaultReportingPeriodOptions,
      ...lastYearReportingOption,
      ...allTimeReportingOption,
    ] /* as readonly Option<string>[] */;
  }, [reportingPeriod]);
}
